<template>
  <v-dialog
    v-model="visible"
    persistent
    max-width="720"
    scrollable
    content-class="parchment-history-dialog"
    @click:outside="close"
  >
    <v-card class="parchment-history-card">
      <v-toolbar color="secondary" class="white--text" flat height="48">
        <v-toolbar-title class="font-weight-bold">
          {{ $t("memberDataSection.parchmentDelivery.viewHistory") }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon class="white--text" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-0 parchment-history-dialog__body">
        <div v-if="loading" class="d-flex justify-center py-10">
          <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
        </div>

        <div
          v-else-if="!historyEntries.length"
          class="text-center py-10 text--secondary"
        >
          {{ $t("memberDataSection.parchmentDelivery.historyEmpty") }}
        </div>

        <div v-else class="pa-6 history-list">
          <div
            v-for="entry in historyEntries"
            :key="entry.id"
            class="history-card"
          >
            <div class="history-card__header">
              <span class="history-card__action-label">{{ entry.actionLabel }}</span>
            </div>

            <div class="history-card__body">
              <div class="history-updated-by mb-1">
                {{ $t("memberDataSection.parchmentDelivery.updatedBy") }}
              </div>
              <div class="history-updated-meta">
                <span>{{ entry.updatedBy }} ({{ entry.updatedByRole }})</span>
                <span class="history-sep">-</span>
                <span>{{ entry.updatedAt }}</span>
              </div>

              <v-divider class="my-4"></v-divider>

              <div class="history-fields">
                <div
                  v-for="field in entry.fields"
                  :key="field.key"
                  class="history-field"
                >
                  <div class="history-field__label">{{ field.label }}</div>
                  <div class="history-field__value">{{ field.value }}</div>
                </div>

                <div v-if="entry.status" class="history-field">
                  <div class="history-field__label">{{ $t("status") }}</div>
                  <div
                    class="history-status-pill"
                    :class="`history-status-pill--${entry.status.type}`"
                  >
                    {{ entry.status.label }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="px-6 py-3 d-flex justify-end">
        <v-btn
          color="primary"
          height="36"
          depressed
          class="text-uppercase white--text px-5"
          @click="close"
        >
          {{ $t("memberDataSection.greenCoffee.close") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ParchmentDeliveryService from "@/_services/ParchmentDeliveryService";

export default {
  name: "ParchmentDeliveryHistoryDialog",
  props: {
    value: { type: Boolean, default: false },
    record: { type: Object, default: null },
  },
  data() {
    return {
      visible: this.value,
      loading: false,
      historyEntries: [],
    };
  },
  watch: {
    value(v) {
      this.visible = v;
      if (v && this.record) {
        this.fetchHistory();
      }
    },
    visible(v) {
      this.$emit("input", v);
    },
  },
  methods: {
    async fetchHistory() {
      const id = this.record?.id ?? this.record?.parchmentDeliveryId;
      if (!id) return;
      this.loading = true;
      this.historyEntries = [];
      try {
        const res = await ParchmentDeliveryService.getParchmentDeliveryHistory(id);
        this.historyEntries = res?.data?.rows || [];
      } catch {
        this.$notify({
          type: "error",
          title: this.$t("error"),
          text: this.$t("memberDataSection.parchmentDelivery.historyLoadError"),
        });
      } finally {
        this.loading = false;
      }
    },
    close() {
      this.visible = false;
      this.$emit("close");
    },
  },
};
</script>

<style lang="scss" scoped>
.parchment-history-card {
  border-radius: 4px;
  overflow: hidden;
  font-family: "Poppins", sans-serif;
}

.parchment-history-dialog__body {
  max-height: calc(80vh - 120px);
  overflow-y: auto;
  background: #fff;
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.history-card {
  border: 1px solid #c0c0c0;
  border-radius: 6px;
  overflow: hidden;
}

.history-card__header {
  background: #ebebeb;
  border-bottom: 1px solid #cdcdcd;
  padding: 10px 18px;
}

.history-card__action-label {
  font-weight: 600;
  font-size: 16px;
  color: #117443;
}

.history-card__body {
  padding: 18px;
}

.history-updated-by {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.54);
}

.history-updated-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  flex-wrap: wrap;
}

.history-sep {
  color: rgba(0, 0, 0, 0.54);
}

.history-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px 34px;
}

.history-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.history-field__label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.54);
}

.history-field__value {
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
}

.history-status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 4px 16px;
  border-radius: 999px;
  border: 1px solid transparent;
  font-size: 13px;
  font-weight: 500;
  width: fit-content;
}

.history-status-pill--success {
  background-color: #e6fff5;
  border-color: #117443;
  color: #117443;
}

.history-status-pill--issues {
  background-color: #fff7ec;
  border-color: #ffa826;
  color: #ffa826;
}

.history-status-pill--failed {
  background-color: #f8d7da;
  border-color: #dc3545;
  color: #dc3545;
}

@media (max-width: 480px) {
  .history-fields {
    grid-template-columns: 1fr;
  }
}
</style>
